/**
 * The fullscreen meeting-grid packing math (the "one participant sits tiny in the corner"
 * fix): given the live tile count and the pixel size of the area between the top bar and the
 * control bar, choose the column x row arrangement that maximizes the size of equally
 * proportioned 16:9 tiles. The caller renders every tile at the returned size and centers the
 * group in the container, so leftover space letterboxes around the grid instead of showing
 * empty background columns - tiles GROW and SHRINK as people join and leave, like Meet/Zoom.
 *
 * Pure and synchronous: the MeetingLayout component feeds it from a ResizeObserver and never
 * lets the grid overflow (every computed rect fits the measured container by construction).
 */

/** Every meeting tile is 16:9 (matches VideoTile's aspect-video and ScreenTile's contain box). */
export const TILE_ASPECT = 16 / 9;

/** Tile spacing in px (Tailwind gap-3, the room's tile gutter token). */
export const TILE_GAP_PX = 12;

/** A computed grid arrangement: grid shape plus the pixel size of one tile. */
export interface GridLayout {
  readonly cols: number;
  readonly rows: number;
  readonly tileWidth: number;
  readonly tileHeight: number;
}

/** The zero grid: nothing to render (empty room or an unmeasured 0x0 container). */
export const EMPTY_GRID: GridLayout = { cols: 0, rows: 0, tileWidth: 0, tileHeight: 0 };

/**
 * Packs `count` 16:9 tiles into the container, maximizing the tile area.
 *
 * For every candidate column count (1..count, rows = ceil(count/cols)) the tile width is
 * bounded by the free width and by the free height (scaled back through the 16:9 aspect);
 * the arrangement with the largest tile width wins. Candidates are visited in ascending
 * column order and only a STRICTLY larger tile replaces the incumbent, so ties resolve to
 * the arrangement with fewer columns (wider rows), which keeps the choice stable across
 * sub-pixel jitter.
 */
export function computeGridLayout(
  count: number,
  containerWidth: number,
  containerHeight: number,
  gap: number = TILE_GAP_PX,
): GridLayout {
  if (count <= 0 || containerWidth <= 0 || containerHeight <= 0) {
    return EMPTY_GRID;
  }
  let best = EMPTY_GRID;
  for (let cols = 1; cols <= count; cols += 1) {
    const rows = Math.ceil(count / cols);
    const widthBound = (containerWidth - (cols - 1) * gap) / cols;
    const heightBound = ((containerHeight - (rows - 1) * gap) / rows) * TILE_ASPECT;
    const tileWidth = Math.floor(Math.min(widthBound, heightBound));
    if (tileWidth > best.tileWidth) {
      best = { cols, rows, tileWidth, tileHeight: Math.floor(tileWidth / TILE_ASPECT) };
    }
  }
  return best;
}

/**
 * The single-row variant used by the mobile strip: exactly `count` columns in one row, tiles
 * shrunk until the whole row fits the container - every participant stays visible without a
 * horizontal scrollbar. Returns the zero grid when nothing fits.
 */
export function computeRowLayout(
  count: number,
  containerWidth: number,
  containerHeight: number,
  gap: number = TILE_GAP_PX,
): GridLayout {
  if (count <= 0 || containerWidth <= 0 || containerHeight <= 0) {
    return EMPTY_GRID;
  }
  const widthBound = (containerWidth - (count - 1) * gap) / count;
  const tileWidth = Math.floor(Math.min(widthBound, containerHeight * TILE_ASPECT));
  if (tileWidth <= 0) {
    return EMPTY_GRID;
  }
  return { cols: count, rows: 1, tileWidth, tileHeight: Math.floor(tileWidth / TILE_ASPECT) };
}
